// angular
import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
} from "@angular/common/http";
import {Observable, throwError} from "rxjs";
import {catchError} from "rxjs/operators";
import {inject} from "@angular/core";
import {Router} from "@angular/router";
// services
import {ErrorHandlerService} from "@shared/services/error-handler.service";
import {StorageService} from "@shared/services/storage.service";
import {environment} from "@environments/environment";

export const BaseInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  // flag to prevent multiple 401 redirects
  let isRedirectingToLogin = false;

  const errorHandlerService = inject(ErrorHandlerService);
  const storageService = inject(StorageService);
  const router = inject(Router);

  // Define public/auth routes that don't require authentication
  const authRoutes = [
    "/login",
    "/signup",
    "/register",
    "/forgot-password",
    "/reset-password",
  ];
  // Decide auth-skip based ONLY on the API endpoint being called, never on the
  // current router URL. Using router.url here is a race: right after login,
  // `employees/info` is fired while the router is still on `/login`, which made
  // this request skip the Authorization header, 401, and wipe the fresh token.
  const isAuthRequest = authRoutes.some((route) => req.url.includes(route));

  // Check if request is for assets (images, fonts, etc.)
  const isAssetRequest =
    req.url.startsWith(".") ||
    req.url.match(/\.(jpg|jpeg|png|gif|svg|ico|css|js|woff|woff2|ttf|eot)$/i);

  const isAbsoluteUrl = /^https?:\/\//i.test(req.url);

  // If already redirecting to login due to 401, reject all non-asset requests
  if (isRedirectingToLogin && !isAssetRequest && !isAuthRequest) {
    return throwError(() => new Error("Unauthorized - Redirecting to login"));
  }

  // skip internal processing for assets and external absolute urls
  if (isAssetRequest || isAbsoluteUrl) {
    return next(req);
  }

  // Validate token for protected routes
  if (!isAuthRequest) {
    const token = storageService.getToken();

    if (!token) {
      storageService.removeToken();
      router.navigate(["/login"]);
      return throwError(() => new Error("Invalid or expired token"));
    }
  }

  let copyReq = req.clone();

  // Prepare headers
  const headers: any = {
    branch: storageService.getBranch(),
    version: `${environment.apiVersion}`,
    "ngrok-skip-browser-warning": "true",
  };

  // Add authorization header for authenticated requests
  if (!isAuthRequest) {
    const token = storageService.getToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  // Handle URL construction based on request type
  if (!req.url.startsWith(".")) {
    copyReq = req.clone({
      url: `${environment.baseUrl}${req.url}`,
      setHeaders: headers,
    });
  } else {
    copyReq = req.clone({
      url: req.url,
      setHeaders: headers,
    });
  }

  return next(copyReq).pipe(
    catchError((err: any) => {
      // Handle 401 Unauthorized errors
      if (err?.error?.statusCode === 401 || err?.status === 401) {
        // Set flag to prevent subsequent requests
        if (!isRedirectingToLogin) {
          isRedirectingToLogin = true;
          storageService.removeToken();

          // Navigate to login
          router.navigate(["/login"]).then(() => {
            // Reset flag after navigation completes
            setTimeout(() => {
              isRedirectingToLogin = false;
            }, 1000);
          });
        }

        return throwError(() => err);
      }

      // Handle other errors
      errorHandlerService.httpErrorHandler(err);
      return throwError(() => err);
    })
  );
};

// Validates if token is still valid (not expired)
function isTokenValid(token: string): boolean {
  try {
    // Decode JWT token
    const payload = JSON.parse(atob(token.split(".")[1]));

    // Check if token has expiration
    if (!payload.exp) {
      return true; // No expiration set, consider valid
    }

    // Check if token is expired (exp is in seconds, Date.now() is in milliseconds)
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp > currentTime;
  } catch (error) {
    console.error("Error validating token:", error);
    return false;
  }
}
